import React, { useState, useEffect } from 'react';
import { XMarkIcon, PhotoIcon, PlusIcon, TrashIcon, ArrowUpIcon, ArrowDownIcon, TruckIcon } from '@heroicons/react/24/outline';
import { Gallery, GalleryFormData, GalleryImage } from '../../types/gallery';
import { galleryService } from '../../services/galleryService';
import { useAuth } from '../../contexts/AuthContext';
import { storage } from '../../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import { Vehicle } from '../../types/vehicle';
import { getVehicles } from '../../services/vehicleService';

interface GalleryEditorProps {
  gallery: Gallery | null;
  onClose: () => void;
}

const GalleryEditor: React.FC<GalleryEditorProps> = ({ gallery, onClose }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  
  const [formData, setFormData] = useState<GalleryFormData>({
    title: '',
    description: '',
    slug: '',
    isPublished: false,
    coverImageUrl: '',
    vehicleIds: [],
    images: []
  });

  useEffect(() => {
    if (gallery) {
      setFormData({
        title: gallery.title,
        description: gallery.description || '',
        slug: gallery.slug,
        isPublished: gallery.isPublished,
        coverImageUrl: gallery.coverImageUrl || '',
        vehicleIds: gallery.vehicleIds || [],
        images: gallery.images.map(img => ({
          id: img.id,
          url: img.url,
          thumbnailUrl: img.thumbnailUrl,
          title: img.title,
          description: img.description,
          order: img.order,
          uploadedAt: img.uploadedAt
        }))
      });
    }
  }, [gallery]);

  useEffect(() => {
    loadVehicles();
  }, [user]);

  const loadVehicles = async () => {
    if (!user?.organizationId) return;
    
    try {
      setLoadingVehicles(true);
      const data = await getVehicles(user.organizationId);
      // Csak a bérlésre elérhető járműveket mutatjuk
      setVehicles(data.filter(v => v.rentalAvailable));
    } catch (error) {
      console.error('Error loading vehicles:', error);
      toast.error('Hiba a járművek betöltése során');
    } finally {
      setLoadingVehicles(false);
    }
  };

  const handleTitleChange = (title: string) => {
    setFormData(prev => ({
      ...prev,
      title,
      slug: galleryService.generateSlug(title)
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !user?.organizationId) return;

    setUploading(true);
    try {
      const uploadPromises = Array.from(files).map(async (file, index) => {
        const timestamp = Date.now();
        const fileName = `galleries/${user.organizationId}/${timestamp}_${file.name}`;
        const storageRef = ref(storage, fileName);
        
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        
        return {
          id: `img_${timestamp}_${index}`,
          url,
          thumbnailUrl: url,
          title: file.name,
          description: '',
          order: (formData.images?.length || 0) + index,
          uploadedAt: new Date()
        };
      });

      const newImages = await Promise.all(uploadPromises);
      setFormData(prev => ({
        ...prev,
        images: [...(prev.images || []), ...newImages]
      }));
      
      toast.success(`${newImages.length} kép feltöltve`);
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Hiba a képek feltöltése során');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images?.filter((_, i) => i !== index)
    }));
  };

  const handleMoveImage = (index: number, direction: 'up' | 'down') => {
    if (!formData.images) return;
    
    const newImages = [...formData.images];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newImages.length) return;
    
    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
    
    // Update order
    newImages.forEach((img, i) => {
      img.order = i;
    });
    
    setFormData(prev => ({
      ...prev,
      images: newImages
    }));
  };

  const handleUpdateImageData = (index: number, field: 'title' | 'description', value: string) => {
    if (!formData.images) return;
    
    const newImages = [...formData.images];
    newImages[index] = {
      ...newImages[index],
      [field]: value
    };
    
    setFormData(prev => ({
      ...prev,
      images: newImages
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.organizationId) {
      toast.error('Nincs bejelentkezve');
      return;
    }

    if (!formData.title.trim()) {
      toast.error('A cím megadása kötelező');
      return;
    }

    setLoading(true);
    try {
      if (gallery) {
        await galleryService.updateGallery(gallery.id, formData);
        toast.success('Galéria sikeresen frissítve');
      } else {
        await galleryService.createGallery(formData, user.uid, user.organizationId);
        toast.success('Galéria sikeresen létrehozva');
      }
      onClose();
    } catch (error: any) {
      console.error('Error saving gallery:', error);
      toast.error(`Hiba: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={gallery ? 'Galéria szerkesztése' : 'Új galéria'}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Basic Info */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Cím *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Slug (URL)
            </label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Leírás
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isPublished"
              checked={formData.isPublished}
              onChange={(e) => setFormData(prev => ({ ...prev, isPublished: e.target.checked }))}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="isPublished" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Publikálva (megjelenik a weboldalon)
            </label>
          </div>
        </div>

        {/* Vehicle Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <TruckIcon className="w-4 h-4 inline mr-1" />
            Hozzárendelt járművek
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Válaszd ki, mely járművek termékoldalaira jelenjen meg ez a galéria
          </p>
          
          {loadingVehicles ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">Járművek betöltése...</div>
          ) : vehicles.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Nincs elérhető jármű. Csak a bérlésre elérhető járműveket lehet kiválasztani.
            </div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              {vehicles.map((vehicle) => (
                <label
                  key={vehicle.id}
                  className="flex items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={formData.vehicleIds?.includes(vehicle.id) || false}
                    onChange={(e) => {
                      const newVehicleIds = e.target.checked
                        ? [...(formData.vehicleIds || []), vehicle.id]
                        : (formData.vehicleIds || []).filter(id => id !== vehicle.id);
                      setFormData(prev => ({ ...prev, vehicleIds: newVehicleIds }));
                    }}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    {vehicle.make} {vehicle.model} ({vehicle.year}) - {vehicle.licensePlate}
                  </span>
                </label>
              ))}
            </div>
          )}
          
          {formData.vehicleIds && formData.vehicleIds.length > 0 && (
            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              {formData.vehicleIds.length} jármű kiválasztva
            </div>
          )}
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Képek
          </label>
          
          <div className="mb-4">
            <label className="flex items-center justify-center w-full h-32 px-4 transition bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg appearance-none cursor-pointer hover:border-primary-400 focus:outline-none">
              <div className="flex flex-col items-center space-y-2">
                <PhotoIcon className="w-8 h-8 text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {uploading ? 'Feltöltés...' : 'Kattints vagy húzd ide a képeket'}
                </span>
              </div>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
                className="hidden"
              />
            </label>
          </div>

          {/* Image List */}
          {formData.images && formData.images.length > 0 && (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {formData.images.map((image, index) => (
                <div
                  key={image.id}
                  className="flex gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  {/* Thumbnail */}
                  <img
                    src={image.url}
                    alt={image.title || `Image ${index + 1}`}
                    className="w-20 h-20 object-cover rounded"
                  />

                  {/* Info */}
                  <div className="flex-1 space-y-2">
                    <input
                      type="text"
                      value={image.title || ''}
                      onChange={(e) => handleUpdateImageData(index, 'title', e.target.value)}
                      placeholder="Kép címe"
                      className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                    <input
                      type="text"
                      value={image.description || ''}
                      onChange={(e) => handleUpdateImageData(index, 'description', e.target.value)}
                      placeholder="Leírás"
                      className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => handleMoveImage(index, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-600 dark:text-gray-400 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ArrowUpIcon className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveImage(index, 'down')}
                      disabled={index === formData.images!.length - 1}
                      className="p-1 text-gray-600 dark:text-gray-400 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ArrowDownIcon className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="p-1 text-red-600 dark:text-red-400 hover:text-red-700"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Mégse
          </button>
          <button
            type="submit"
            disabled={loading || uploading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Mentés...' : gallery ? 'Frissítés' : 'Létrehozás'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default GalleryEditor;
